/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { useInterval } from '../../../../shared/useInterval/useInterval';
import useMounted from '../../../../shared/useMount/useMount';
import {
  POLLING_SECONDS,
  POLLING_MILLI_SECONDS
} from '../../../../shared/Utils/Constants';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  ISiteCard,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageHOCProps,
  IEuiSummary,
  IEuiV4,
  IAny
} from '../../../IEnergyManagementProps';
import PortfolioEnergyCard from '../PortfolioEnergyCard/PortfolioEnergyCard';
import EmaasBasicSubscriptionEnergyUsageComponent from '../../../EmaasEnergyUsage/EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponent';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import {
  IPollingContext,
  PollingContext
} from '../../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import moment from 'moment';
import NoData from '../../../../shared/ErrorComponents/NoData';

interface IPortfolioEnergyHOCContent extends ISiteCard {
  data: IEmaasEnergyUsageComponentData;
  optimusSubscribed?: boolean;
  isEnergyDeatils?: Function;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: IAny;
}

const PortfolioEnergyHOCContent = (props: IPortfolioEnergyHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    optimusSubscribed,
    isEnergyDeatils,
    checkAllSitesAndMonthData
  } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  const handleNavigation = () => {
    if (optimusSubscribed) {
      isEnergyDeatils && isEnergyDeatils();
    }
  };
  return (
    <div onClick={handleNavigation}>
      {data.subscriptionType === EnergyUsageField.BASICSUBS ? (
        <EmaasBasicSubscriptionEnergyUsageComponent
          data={data}
          viewType={EnergyUsageField.PORTFOLIOENERGY}
          optimusSubscribed={optimusSubscribed}
          checkAllSitesAndMonthData={checkAllSitesAndMonthData}
          dataSourceType={props.dataSourceType}
        />
      ) : (
        <PortfolioEnergyCard
          data={data}
          viewType={EnergyUsageField.PORTFOLIOENERGY}
          optimusSubscribed={optimusSubscribed}
          checkAllSitesAndMonthData={checkAllSitesAndMonthData}
          dataSourceType={props.dataSourceType}
        />
      )}
    </div>
  );
};

const PortfolioEnergyHOCWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioEnergyHOCContent)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;

const PortfolioEnergyHOC = (props: IEmaasEnergyUsageHOCProps) => {
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);
  const {
    bearerToken,
    requestContext,
    dataSourceType,
    optimusSubscribed,
    isEnergyDeatils
  } = props;
  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    undefined,
    undefined,
    dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: 'portfolio-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate),
      dataSource: dataSourceType?.valueOf() || ''
    }
  };

  const connectorConfigEui = {
    name: 'portfolio-energy-usage-eui',
    options: {
      currentDate: moment(new Date())
        .subtract(1, 'months')
        .startOf('month')
        .format('YYYY-MM-DD')
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const dataConnectorEui = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigEui,
    requestContext,
    bearerToken
  })[0];
  const onRefresh = () => {
    dataConnectorEui?.actions?.getAll();
    return dataConnector?.actions?.getAll();
  };

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount, dataSourceType]);

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const { all, loading, error, status } = dataConnector.data;
  const allEui = dataConnectorEui.data.all;
  const loadingEui = dataConnectorEui.data.loading;
  const errorEui = dataConnectorEui.data.error;
  const statusEui = dataConnectorEui.data.status;

  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };

  const EmaasEnergyUsage = (all as unknown) as IEmaasEnergyUsageComponentData;
  const EmaasEnergyUsageEui = (allEui as unknown) as IEuiV4;

  // Polling mechanism
  useInterval(() => {
    if (
      EmaasEnergyUsage &&
      EmaasEnergyUsage.subscriptionType === EnergyUsageField.PREMIUMSUBS
    ) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const isDataAvailable = EmaasEnergyUsage ? true : false;
  const isDataAvailableEui = EmaasEnergyUsageEui ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentData);
  const pollingContext = useContext(PollingContext);

  const [isLoadingEui, setIsLoadingEui] = useState<boolean>(false);
  const [statusCodeEui, setStatusCodeEui] = useState<number | null>(null);
  const [errorInfoEui, setErrorInfoEui] = useState(errorEui);
  const [checkAllSitesAndMonthData, setCheckAllSitesAndMonthData] = useState<
    boolean
  >(true);

  const checkIfAllSitesHasCompleteData = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      data.monthWiseAggregations.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalBaselineConsumption !== 0 &&
          item.totalBaselineConsumption !== null &&
          item.totalConsumption !== 0 &&
          item.totalConsumption !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalConsumption !== 0 &&
          item.totalBaselineConsumption !== 0 &&
          item.totalConsumption !== null &&
          item.totalBaselineConsumption !== null
      )
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        if (
          (dataSourceType &&
            dataSourceType === EnergyUsageDataSourceType.UTILITY_METER) ||
          !dataSourceType
        ) {
          setCheckAllSitesAndMonthData(
            checkIfAllSitesHasPreviousPeriod(EmaasEnergyUsage) &&
              checkIfAllMonthsHasPreviousPeriod(EmaasEnergyUsage) &&
              checkIfAllMonthHasCompleteData(EmaasEnergyUsage) &&
              checkIfAllSitesHasCompleteData(EmaasEnergyUsage)
          );
        } else {
          setCheckAllSitesAndMonthData(true);
        }
        setData(EmaasEnergyUsage);
        // Disable EUI update for live meter dropdown
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setIsLoadingEui(loadingEui);
    if ((isDataAvailableEui || errorEui || statusEui) && !loadingEui) {
      if (isDataAvailableEui) {
        setContextData(pollingContext, EmaasEnergyUsageEui);
      }
      setErrorInfoEui(errorEui);
    }
    setStatusCodeEui(statusEui);
  }, [isDataAvailableEui, loadingEui, statusEui]);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <PortfolioEnergyHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={removeSilentLoadingonRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      silentLoading={silentLoading}
      optimusSubscribed={optimusSubscribed}
      isEnergyDeatils={isEnergyDeatils}
      checkAllSitesAndMonthData={checkAllSitesAndMonthData}
      dataSourceType={dataSourceType}
    />
  );
};

const setContextData = (
  pollingContext: IPollingContext,
  EmaasEnergyUsage: IEuiV4
) => {
  const { setPollingTime, setEuiSummary } = pollingContext;

  setPollingTime && setPollingTime();
  setEuiSummary &&
    setEuiSummary(
      (EmaasEnergyUsage &&
        EmaasEnergyUsage.data &&
        EmaasEnergyUsage.data.euiSummary) ||
        ({} as IEuiSummary)
    );
};

export default PortfolioEnergyHOC;
